import { Option, Schema as S } from "effect";
import * as AsyncData from "foldkit/asyncData";

import { apiData } from "../../api/asyncData";
import { toImportContact } from "../../api/commands/import";
import {
	CommitResult,
	ImportBatches,
	ImportRow,
	ImportSource,
	type MatchCandidate,
	MatchResult,
	UndoPreview,
} from "../../api/schemas/import";
import type { ParsedContact } from "../../import/contact";
import { Dialog } from "../../widgets/dialog";

/**
 * The one-shot import wizard (D7): Pick → Preview → Review → Committing →
 * Summary. Nothing is written until the user confirms, every likely duplicate
 * is reviewed first (D2), and the preview opens with nothing selected (D3).
 * History is off to one side: past batches (R18) and their undo (R19).
 */

export const Step = S.Literals([
	"Pick",
	"Preview",
	"Review",
	"Committing",
	"Summary",
	"History",
]);
export type Step = typeof Step.Type;

/** `unreadable` is ours; the rest are `parseContactFile`'s own error kinds. */
export const ParseErrorKind = S.Literals([
	"empty",
	"unrecognised",
	"tooLarge",
	"malformed",
	"unreadable",
]);
export type ParseErrorKind = typeof ParseErrorKind.Type;

/** `row` is set only for `malformed`; it is a row number, never the row's text. */
export const ParseError = S.Struct({
	kind: ParseErrorKind,
	row: S.Option(S.Number),
});
export type ParseError = typeof ParseError.Type;

export const ResolutionAction = S.Literals(["merge", "create", "skip"]);
export type ResolutionAction = typeof ResolutionAction.Type;

export const Resolution = S.Struct({
	action: ResolutionAction,
	targetRecordId: S.Option(S.String),
});
export type Resolution = typeof Resolution.Type;

/** Every match request's answer, merged: the last capacity wins, results append. */
export const Matches = S.Struct({
	circleId: S.String,
	remainingRecordCapacity: S.Number,
	results: S.Array(MatchResult),
});
export type Matches = typeof Matches.Type;

/**
 * The parser's row shape (`src/import/contact.ts`) held as-is. It is
 * browser-only data that is never encoded, so a structural check is enough.
 */
const Contact = S.declare<ParsedContact>(
	(value): value is ParsedContact =>
		typeof value === "object" &&
		value !== null &&
		"rowId" in value &&
		"nameKey" in value,
);

export const Model = S.Struct({
	page: S.Literal("Import"),
	step: Step,
	fileName: S.String,
	/**
	 * The file's text is deliberately absent. It reaches the browser, is parsed
	 * once, and is dropped (KTD1, R23) -- holding a 20 MB export for the page's
	 * life would keep every imported contact in memory for nothing.
	 */
	/** Meaningless until a parse succeeds, which is the only way past Pick. */
	source: ImportSource,
	parseError: S.Option(ParseError),
	contacts: S.Array(Contact),
	excluded: S.Struct({ noName: S.Number, duplicate: S.Number }),
	selected: S.ReadonlySet(S.Number),
	filter: S.String,
	pageIndex: S.Number,
	matches: apiData(Matches).schema,
	resolutions: S.ReadonlyMap(S.Number, Resolution),
	batchId: S.String,
	/** The commit rows, frozen at confirm time so a retry resends the same bytes. */
	rows: S.Array(ImportRow),
	skippedRows: S.Number,
	/** The server's whole result list from the last job status, replaced each poll. */
	results: S.Array(CommitResult),
	/** The job's row count as the server last reported it; `rows.length` until then. */
	total: S.Number,
	/** Rows the server says have landed, including those inside the slice it is writing. */
	landed: S.Number,
	/** Submit retries spent, then consecutive poll failures, on the request in flight. */
	attempt: S.Number,
	stoppedReason: S.Option(S.String),
	/* -- History (R18) and undo (R19) ------------------------------------- */
	batches: apiData(ImportBatches).schema,
	dialog: Dialog.Model,
	/** The batch the confirmation dialog is about, if it is open. */
	undoBatchId: S.Option(S.String),
	undoPreview: apiData(UndoPreview).schema,
	/** True from Confirm until the chunked undo answers `done` (or fails). */
	undoing: S.Boolean,
	undoError: S.Option(S.String),
});
export type Model = typeof Model.Type;

/** Preview page size (R5). */
export const PAGE_SIZE = 25;

export const initial = (): Model => ({
	page: "Import",
	step: "Pick",
	fileName: "",
	source: "linkedin",
	parseError: Option.none(),
	contacts: [],
	excluded: { noName: 0, duplicate: 0 },
	selected: new Set(),
	filter: "",
	pageIndex: 0,
	matches: AsyncData.Idle(),
	resolutions: new Map(),
	batchId: "",
	rows: [],
	skippedRows: 0,
	results: [],
	total: 0,
	landed: 0,
	attempt: 0,
	stoppedReason: Option.none(),
	batches: AsyncData.Idle(),
	dialog: Dialog.init({ id: "import_undo_dialog" }),
	undoBatchId: Option.none(),
	undoPreview: AsyncData.Idle(),
	undoing: false,
	undoError: Option.none(),
});

const NO_MATCHES: Matches = {
	circleId: "",
	remainingRecordCapacity: Number.POSITIVE_INFINITY,
	results: [],
};

/** Partial results are visible while the remaining match requests are in flight. */
export const matchesOf = (model: Model): Matches =>
	AsyncData.getOrElse(model.matches, () => NO_MATCHES);

export const circleIdOf = (model: Model): string => matchesOf(model).circleId;

export const capacityOf = (model: Model): number =>
	matchesOf(model).remainingRecordCapacity;

export const matchByRow = (model: Model): ReadonlyMap<number, MatchResult> =>
	new Map(matchesOf(model).results.map((result) => [result.rowId, result]));

/** Filters over name, organisation and email (R5). */
export const filteredContacts = (
	model: Model,
): ReadonlyArray<ParsedContact> => {
	const needle = model.filter.trim().toLowerCase();
	if (needle === "") return model.contacts;
	return model.contacts.filter(
		(contact) =>
			contact.name.toLowerCase().includes(needle) ||
			(contact.organisation ?? "").toLowerCase().includes(needle) ||
			contact.emails.some((email) => email.toLowerCase().includes(needle)),
	);
};

export const pageCount = (model: Model): number =>
	Math.max(1, Math.ceil(filteredContacts(model).length / PAGE_SIZE));

export const pageContacts = (model: Model): ReadonlyArray<ParsedContact> =>
	filteredContacts(model).slice(
		model.pageIndex * PAGE_SIZE,
		model.pageIndex * PAGE_SIZE + PAGE_SIZE,
	);

export type ReviewRow = Readonly<{
	contact: ParsedContact;
	candidates: ReadonlyArray<MatchCandidate>;
}>;

/** Only selected rows the server offered candidates for need a decision (D2). */
export const reviewRows = (model: Model): ReadonlyArray<ReviewRow> => {
	const byRow = matchByRow(model);
	return model.contacts.flatMap((contact) => {
		if (!model.selected.has(contact.rowId)) return [];
		const match = byRow.get(contact.rowId);
		if (
			match === undefined ||
			match.alreadyInCircle !== undefined ||
			match.candidates.length === 0
		)
			return [];
		return [{ contact, candidates: match.candidates }];
	});
};

export const unresolvedRows = (
	model: Model,
	rows = reviewRows(model),
): ReadonlyArray<number> =>
	rows
		.map((row) => row.contact.rowId)
		.filter((rowId) => !model.resolutions.has(rowId));

export type CommitPlan = Readonly<{
	rows: ReadonlyArray<ImportRow>;
	skipped: number;
	creates: number;
	merges: number;
}>;

/**
 * The rows the commit will send, in file order. A row already in the circle is
 * update-only against the record it matched (R7); a merge targets the chosen
 * candidate; everything else creates.
 */
export const commitPlan = (model: Model): CommitPlan => {
	const byRow = matchByRow(model);
	const rows: ImportRow[] = [];
	let skipped = 0;
	let creates = 0;
	let merges = 0;
	for (const contact of model.contacts) {
		if (!model.selected.has(contact.rowId)) continue;
		const resolution = model.resolutions.get(contact.rowId);
		if (resolution?.action === "skip") {
			skipped += 1;
			continue;
		}
		const target =
			byRow.get(contact.rowId)?.alreadyInCircle ??
			(resolution?.action === "merge"
				? Option.getOrUndefined(resolution.targetRecordId)
				: undefined);
		const importContact = toImportContact(contact);
		if (target === undefined) {
			creates += 1;
			rows.push({
				rowId: contact.rowId,
				action: "create",
				contact: importContact,
			});
		} else {
			merges += 1;
			rows.push({
				rowId: contact.rowId,
				action: "update",
				targetRecordId: target,
				contact: importContact,
			});
		}
	}
	return { rows, skipped, creates, merges };
};

/**
 * How many rows the user must deselect for the creates to fit (R24). The plan
 * is a parameter so a caller that already has one does not rebuild the match
 * map and rescan every contact for it.
 */
export const overflow = (model: Model, plan = commitPlan(model)): number =>
	Math.max(0, plan.creates - capacityOf(model));

/** Same reason as `overflow`: the review view computes all three once. */
export const canConfirm = (
	model: Model,
	plan = commitPlan(model),
	pending = unresolvedRows(model).length,
	short = overflow(model, plan),
): boolean =>
	pending === 0 && short === 0 && plan.rows.length + plan.skipped > 0;

export type Summary = Readonly<{
	created: number;
	merged: number;
	failed: number;
	skipped: number;
	excluded: number;
	notAttempted: number;
	landed: number;
	total: number;
}>;

/** R16's counts. `notAttempted` is whatever the run never got an answer for. */
export const summary = (model: Model): Summary => {
	const count = (outcome: string) =>
		model.results.filter((result) => result.outcome === outcome).length;
	const attempted = model.results.filter(
		(result) => result.outcome !== "notAttempted",
	).length;
	return {
		created: count("created"),
		merged: count("updated"),
		failed: count("failed"),
		skipped: model.skippedRows,
		excluded: model.excluded.noName + model.excluded.duplicate,
		notAttempted: model.total - attempted,
		// The server reports progress inside a slice before it stores results,
		// so it can lead the stored count; it can never lag it.
		landed: Math.max(model.landed, attempted),
		total: model.total,
	};
};
